/**
 * Utility functions for handling authentication-related URL parameters
 */

/**
 * Gets the authentication token, checking environment variable first, then Auth0
 * @param getIdTokenClaims - Auth0 function to get token claims (optional, only needed if env var not set)
 * @returns Promise<string | null> - The token string or null if unavailable
 */
export const getAuthToken = async (
  getIdTokenClaims?: () => Promise<unknown>
): Promise<string | null> => {
  // Check for environment variable first (for testing/development)
  const envToken = process.env.REACT_APP_AUTH0_TOKEN;
  if (envToken) {
    return envToken;
  }

  // Fall back to Auth0 if available
  if (getIdTokenClaims) {
    try {
      const tokenClaims = await getIdTokenClaims();
      if (tokenClaims && typeof tokenClaims === 'object' && '__raw' in tokenClaims) {
        const raw = (tokenClaims as { __raw?: string }).__raw;
        return raw || null;
      }
      return null;
    } catch (error) {
      console.warn('Failed to get Auth0 token:', error);
      return null;
    }
  }

  return null;
};

/**
 * Cleans up authentication parameters (code, state) from the current URL
 * This prevents issues when users refresh the page with Auth0 callback parameters
 */
export const cleanupAuthParams = (): void => {
  try {
    const url = new URL(window.location.href);
    const hasAuthParams = url.searchParams.has('code') || url.searchParams.has('state');

    if (hasAuthParams) {
      // Remove auth-related parameters from URL
      url.searchParams.delete('code');
      url.searchParams.delete('state');

      // Replace the current URL without the auth parameters
      window.history.replaceState({}, '', url.toString());
    }
  } catch (error) {
    console.warn('Failed to cleanup auth parameters:', error);
  }
};

/**
 * Checks if the current URL contains authentication parameters
 */
export const hasAuthParams = (): boolean => {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.has('code') || url.searchParams.has('state');
  } catch (error) {
    return false;
  }
};
